import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  IconButton,
  LinearProgress,
  Chip,
} from '@material-ui/core';
import {
  CloudUpload,
  AttachFile,
  Delete,
  Description,
  Image,
  PictureAsPdf,
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import clsx from 'clsx';

import { setPendingUploadArquivos } from '~/store/modules/pautas/actions';
import { useStyles } from './styles';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
export default function ModalUploadArquivos() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { novaPauta, pendingUploadArquivos } = useSelector(
    state => state.pautas
  );
  const unidadeId = useSelector(state => state.unidade.unidade_id);

  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setUploadProgress({});
      return;
    }

    if (pendingUploadArquivos && novaPauta) {
      const sameContext =
        pendingUploadArquivos.unidadeId === unidadeId &&
        pendingUploadArquivos.turmaId === novaPauta.turma &&
        pendingUploadArquivos.materiaId === novaPauta.materia &&
        pendingUploadArquivos.pautaData === novaPauta.data;

      if (sameContext && Array.isArray(pendingUploadArquivos.files)) {
        setFiles(pendingUploadArquivos.files);
      }
    }
  }, [open, pendingUploadArquivos, unidadeId, novaPauta]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (uploading) {
      toast.warn('Aguarde o término do upload antes de fechar.');
      return;
    }
    setOpen(false);
  };

  const handleDrag = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = file => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`O arquivo ${file.name} excede o tamanho máximo de 50MB.`);
      return false;
    }

    const extension = file.name
      .split('.')
      .pop()
      .toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error(
        `O arquivo ${file.name} não é suportado. Apenas arquivos jpg, jpeg, png e pdf são permitidos.`
      );
      return false;
    }

    return true;
  };

  const handleFiles = useCallback(fileList => {
    if (files.length + fileList.length > MAX_FILES) {
      toast.error(`Você pode adicionar no máximo ${MAX_FILES} arquivos.`);
      return;
    }

    const newFiles = Array.from(fileList).filter(validateFile);
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
  });

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleFileInput = e => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = index => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const getFileIcon = fileName => {
    const extension = fileName
      .split('.')
      .pop()
      .toLowerCase();

    if (extension === 'pdf') {
      return <PictureAsPdf className={classes.fileIcon} />;
    }
    if (['jpg', 'jpeg', 'png'].includes(extension)) {
      return <Image className={classes.fileIcon} />;
    }
    return <Description className={classes.fileIcon} />;
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  // Helpers para diagnosticar erros de upload e apresentar mensagens claras ao usuário
  const uploadFiles = () => {
    if (files.length === 0) {
      toast.warn('Selecione pelo menos um arquivo para upload.');
      return;
    }

    if (!novaPauta) {
      toast.warn(
        'Você precisa iniciar/salvar a pauta antes de enviar arquivos.'
      );
      return;
    }

    const alunoIds = novaPauta.alunos.map(aluno => aluno.aluno_id);
    const description = `Comprovante de frequência da aula de ${new Date(
      novaPauta.data
    ).toLocaleDateString('pt-BR')}, da turma ${novaPauta.turma}`;

    dispatch(
      setPendingUploadArquivos({
        unidadeId,
        files,
        description,
        alunoIds,
        pautaData: novaPauta.data,
        turmaId: novaPauta.turma,
        materiaId: novaPauta.materia,
      })
    );

    toast.info(
      'Os arquivos serão enviados automaticamente após salvar a pauta.'
    );

    setOpen(false);
    setFiles([]);
    setUploadProgress({});
    setUploading(false);
  };

  return (
    <div>
      <Button
        variant="text"
        startIcon={<AttachFile />}
        color="primary"
        className={classes.btUpload}
        onClick={handleClickOpen}
      >
        Upload de Arquivos
      </Button>
      <Dialog
        open={open}
        scroll="paper"
        aria-labelledby="upload-dialog-title"
        aria-describedby="upload-dialog-description"
      >
        <DialogTitle id="upload-dialog-title">Upload de Arquivos</DialogTitle>
        <DialogContent className={classes.content}>
          <Typography variant="body2" gutterBottom>
            Faça upload de arquivos para comprovação de frequência. Você pode
            adicionar até {MAX_FILES} arquivos.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Formatos permitidos: JPG, JPEG, PNG, PDF. Tamanho máximo por
            arquivo: 50MB.
          </Typography>

          <div
            className={clsx(
              classes.dropzone,
              dragActive && classes.dropzoneActive
            )}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const input = document.getElementById('file-input');
                if (input) input.click();
              }
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              multiple
              onChange={handleFileInput}
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: 'none' }}
            />
            <CloudUpload fontSize="large" color="primary" />
            <Typography variant="body1" gutterBottom>
              Arraste e solte arquivos aqui ou clique para selecionar
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Apenas arquivos JPG, JPEG, PNG e PDF são aceitos
            </Typography>
          </div>

          {files.length > 0 && (
            <div className={classes.fileList}>
              <Typography variant="subtitle1" gutterBottom>
                Arquivos selecionados ({files.length}/{MAX_FILES})
              </Typography>

              {files.map((file, index) => (
                <div
                  key={`${file.name}-${file.size}-${file.lastModified || ''}`}
                  className={classes.fileItem}
                >
                  <div className={classes.fileInfo}>
                    {getFileIcon(file.name)}
                    <Typography className={classes.fileName} noWrap>
                      {file.name}
                    </Typography>
                    <Typography className={classes.fileSize}>
                      {formatFileSize(file.size)}
                    </Typography>
                  </div>

                  <div>
                    {uploading ? (
                      <Chip
                        label={`${uploadProgress[index] || 0}%`}
                        color={
                          uploadProgress[index] === 100 ? 'primary' : 'default'
                        }
                      />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => removeFile(index)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </div>
                </div>
              ))}

              {uploading && (
                <LinearProgress
                  className={classes.uploadProgress}
                  variant="determinate"
                  value={
                    Object.values(uploadProgress).reduce(
                      (sum, value) => sum + value,
                      0
                    ) / (files.length || 1)
                  }
                />
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            variant="contained"
            disabled={uploading}
          >
            Cancelar
          </Button>
          <Button
            onClick={uploadFiles}
            color="primary"
            variant="contained"
            disabled={files.length === 0 || uploading}
            startIcon={<CloudUpload />}
          >
            {uploading ? 'Enviando...' : 'CONFIRMAR'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
